import React, { useState } from 'react';
import {
  Type, Palette, MousePointer2, MessageSquare,
  FileText, PlayCircle, Download, Copy, Check,
  LayoutGrid, Bell, User, Paperclip, Send,
  Globe
} from 'lucide-react';


/* ===================================================================
   MASTER CSS (BOOTSTRAP 5.3 INTEGRATED)
   - Unified Design System v5.3
   - Features: Stable Gutter, Native Bootstrap Variables, Sticky Footer
   - Updated: Forced Scrollbar to prevent layout jumping
   =================================================================== */
const MASTER_CSS = `
@charset "UTF-8";
@import url("https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css");
@import url("https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap");


/* --- 1. TOKENS & BOOTSTRAP OVERRIDES --- */
:root {
  /* Brand */
  --brand-primary: #0d6efd;
  --brand-bg: #f5f8ff;
  --brand-text: #212529;
 
  /* Semantic */
  --color-info-bg: #eef2ff;
  --color-success-bg: #d1e7dd; --color-success-text: #198754;
  --color-warning-bg: #fff3cd; --color-warning-text: #856404;
  --color-danger-bg: #f8d7da; --color-danger-text: #dc3545;
 
  /* UI Neutrals */
  --ui-gray-bg: #F2F4F7;
  --ui-border: #d0d5dd;
  --ui-text-muted: #667085;


  /* Dimensions */
  --radius-md: 0.5rem;
  --font-base: 14px;


  /* Bootstrap Variable Mapping */
  --bs-primary: var(--brand-primary);
  --bs-primary-rgb: 13, 110, 253;
  --bs-body-font-family: 'Inter', sans-serif;
  --bs-body-bg: var(--ui-gray-bg);
  --bs-body-color: #475467;
  --bs-border-radius: var(--radius-md);
  --bs-link-color: var(--brand-primary);
  --bs-link-hover-color: #0b5ed7;
}


/* --- 2. BASE STYLES --- */
html {
  font-size: 16px;
  scroll-behavior: smooth;
  /* Force vertical scrollbar to always exist, preventing width jumps */
  overflow-y: scroll;
}


body {
  margin: 0; padding: 0;
  font-family: var(--bs-body-font-family) !important;
  font-size: var(--font-base);
  background-color: var(--bs-body-bg);
  -webkit-font-smoothing: antialiased;
  display: flex; flex-direction: column; min-height: 100vh;
  overflow-x: hidden;
}


/* Scrollbar */
::-webkit-scrollbar { width: 8px; height: 8px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
::-webkit-scrollbar-thumb:hover { background: #94a3b8; }


/* Typography */
h1, h2, h3, h4, h5, h6 { font-weight: 600; color: #101828; margin-bottom: 0.5rem; }
a { text-decoration: none; color: inherit; }
.fw-bold { font-weight: 700 !important; }
.fw-medium { font-weight: 500 !important; }
.text-xs { font-size: 12px; line-height: 18px; }
.text-sm { font-size: 13px; }
.text-muted { color: var(--ui-text-muted) !important; }
.text-primary { color: var(--brand-primary) !important; }
.text-danger { color: #f97066 !important; }


/* --- 3. LAYOUT SHELL --- */
.main-wrapper {
  background-color: var(--brand-bg);
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}


.layout-container {
  width: 100%;
  max-width: 1400px;
  margin: 0 auto;
  padding: 30px;
  flex: 1;
}


/* Header */
.app-header {
  background: #fff; border-bottom: 1px solid var(--ui-border);
  padding: 0.75rem 2rem; display: flex; justify-content: space-between; align-items: center;
  box-shadow: 0 1px 2px rgba(0,0,0,0.05); position: sticky; top: 0; z-index: 1000;
}
.app-logo-img { height: 32px; width: auto; margin-right: 1.5rem; display: block; }


/* Navigation Tabs */
.app-nav {
  background: #fff; border-bottom: 1px solid var(--ui-border);
  padding: 0 2rem; display: flex; gap: 2rem;
}
.app-nav-link {
  padding: 1rem 0.5rem; font-weight: 600; color: var(--ui-text-muted); font-size: 14px;
  background: none; border: none; border-bottom: 3px solid transparent; cursor: pointer;
  transition: all 0.2s; display: flex; align-items: center; gap: 8px;
}
.app-nav-link:hover { color: var(--brand-text); }
.app-nav-link.active { color: var(--brand-primary); border-bottom-color: var(--brand-primary); }


.content-area {
  padding: 1.5rem; background: #fff; border-radius: var(--radius-md);
  box-shadow: 0 1px 3px rgba(0,0,0,0.05); margin-bottom: 1.5rem; border: 1px solid var(--ui-border);
}


/* Footer */
.app-footer {
  background: #fff; border-top: 1px solid var(--ui-border);
  padding: 1.5rem 2rem; display: flex; justify-content: center; align-items: center;
  margin-top: auto; z-index: 10;
}
.footer-link {
  color: var(--ui-text-muted); font-size: 13px; font-weight: 500;
  display: flex; align-items: center; gap: 6px; transition: color 0.2s;
}
.footer-link:hover { color: var(--brand-primary); }


/* --- 4. COMPONENTS --- */


/* Buttons */
.btn {
  font-weight: 600; padding: 0.6rem 1.2rem; border-radius: var(--radius-md);
  display: inline-flex; align-items: center; justify-content: center; gap: 0.5rem;
  transition: 0.2s; font-size: 14px; white-space: nowrap;
}
.btn-primary { background-color: var(--brand-primary); border-color: var(--brand-primary); box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05); }
.btn-primary:hover { background-color: #0b5ed7; transform: translateY(-1px); box-shadow: 0 4px 6px rgba(13, 110, 253, 0.15); }
.btn-outline-primary { color: var(--brand-primary); border-color: var(--brand-primary); }
.btn-outline-primary:hover { background-color: var(--color-info-bg); color: var(--brand-primary); border-color: var(--brand-primary); }
.btn-icon { padding: 8px; color: var(--ui-text-muted); border: 1px solid var(--ui-border); background: #fff; }
.btn-icon:hover { color: var(--brand-primary); border-color: var(--brand-primary); background: var(--color-info-bg); }


/* Forms */
.form-control, .form-select {
  color: #344054; font-size: 14px; font-weight: 500;
  border-radius: 8px; min-height: 42px; border: 1px solid var(--ui-border);
  padding: 0.5rem 0.75rem; box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05);
}
.form-control:focus, .form-select:focus { border-color: var(--brand-primary); box-shadow: 0 0 0 4px rgba(13, 110, 253, 0.15); }
.form-label { font-size: 13px; font-weight: 500; color: #344054; margin-bottom: 6px; }


.otp-input {
  width: 50px; height: 50px; text-align: center; font-size: 1.25rem; font-weight: 600;
  border-radius: 8px; border: 1px solid var(--ui-border); background: #fff; color: var(--brand-text);
}
.otp-input:focus { border-color: var(--brand-primary); box-shadow: 0 0 0 4px rgba(13, 110, 253, 0.15); outline: none; }


/* Cards */
.card-std { background: #fff; border: 1px solid var(--ui-border); border-radius: 12px; padding: 20px; transition: 0.3s; position: relative; }
.card-interactive { cursor: pointer; }
.card-interactive:hover { border-color: var(--brand-primary); background-color: #f8faff; transform: translateY(-2px); box-shadow: 0 4px 12px rgba(0,0,0,0.05); }
.card-interactive.selected { border-color: var(--brand-primary); background-color: var(--color-info-bg); box-shadow: 0 0 0 1px var(--brand-primary) inset; }


/* --- 5. MODULES --- */


/* Kanban */
.kanban-board { display: flex; gap: 20px; overflow-x: auto; padding-bottom: 20px; }
.kanban-col { flex: 0 0 300px; background: #F9FAFB; border-radius: 12px; padding: 16px; border: 1px solid var(--ui-border); }
.kanban-col-header { display: flex; justify-content: space-between; margin-bottom: 16px; font-size: 14px; font-weight: 600; color: #475467; }
.kanban-item {
  background: #fff; border: 1px solid var(--ui-border); border-radius: 8px;
  padding: 16px; margin-bottom: 12px; box-shadow: 0 1px 2px rgba(0,0,0,0.05);
  cursor: grab; position: relative; transition: 0.2s; border-left: 4px solid transparent;
}
.kanban-item:hover { transform: translateY(-2px); box-shadow: 0 10px 15px -3px rgba(0,0,0,0.1); }
.kanban-item.status-success { border-left-color: #198754; }
.kanban-item.status-warning { border-left-color: #f59e0b; }
.kanban-item.status-danger { border-left-color: #dc3545; }


/* Chat */
.chat-window { background: #fff; border: 1px solid var(--ui-border); border-radius: 12px; overflow: hidden; height: 500px; display: flex; flex-direction: column; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1); }
.chat-topbar { padding: 16px; border-bottom: 1px solid var(--ui-border); background: #fff; font-weight: 600; display: flex; justify-content: space-between; align-items: center; }
.chat-viewport { flex: 1; background: var(--ui-gray-bg); padding: 20px; overflow-y: auto; display: flex; flex-direction: column; gap: 16px; }
.chat-msg { max-width: 75%; padding: 12px 16px; border-radius: 12px; font-size: 14px; line-height: 1.5; position: relative; box-shadow: 0 1px 2px rgba(0,0,0,0.05); }
.chat-msg.received { background: #fff; border: 1px solid var(--ui-border); border-bottom-left-radius: 2px; align-self: flex-start; color: var(--brand-text); }
.chat-msg.sent { background: var(--color-info-bg); color: #1e3a8a; border: 1px solid #bfdbfe; border-bottom-right-radius: 2px; align-self: flex-end; }
.chat-timestamp { font-size: 11px; color: var(--ui-text-muted); margin-top: 4px; text-align: right; opacity: 0.8; }
.chat-composer { padding: 16px; background: #fff; border-top: 1px solid var(--ui-border); display: flex; gap: 12px; align-items: center; }


/* Quiz */
.quiz-box {
  background: #fff; border-radius: 16px; padding: 32px 24px; text-align: center;
  border: 2px solid transparent; box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  transition: 0.3s; cursor: pointer; position: relative;
}
.quiz-box:hover { border-color: var(--brand-primary); transform: translateY(-4px); box-shadow: 0 12px 24px rgba(0,0,0,0.1); }
.quiz-box.selected { border-color: var(--brand-primary); background: var(--color-info-bg); }
.quiz-img { width: 64px; height: 64px; margin: 0 auto 16px; background: var(--brand-bg); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: var(--brand-primary); font-size: 24px; }
.quiz-footer { margin-top: 20px; opacity: 0; transition: 0.3s; transform: translateY(10px); }
.quiz-box:hover .quiz-footer { opacity: 1; transform: translateY(0); }


/* File Upload */
.dropzone {
  border: 2px dashed var(--ui-border); border-radius: 12px; padding: 40px 20px;
  text-align: center; cursor: pointer; transition: 0.2s; background: #fff;
}
.dropzone:hover { border-color: var(--brand-primary); background: #f8faff; }
.dropzone-icon { color: var(--ui-text-muted); margin-bottom: 12px; }


/* Media Player */
.video-player {
  background: #111827; border-radius: 12px; overflow: hidden; position: relative;
  min-height: 300px; display: flex; align-items: center; justify-content: center;
  color: #fff; box-shadow: 0 4px 6px -1px rgba(0,0,0,0.1);
}
.video-controls {
  position: absolute; bottom: 0; left: 0; width: 100%; padding: 16px;
  background: linear-gradient(to top, rgba(0,0,0,0.9), transparent);
  display: flex; gap: 12px; align-items: center;
}


/* --- 6. OVERRIDES --- */
.ql-toolbar.ql-snow { border-color: var(--ui-border) !important; background: #f9fafb; }
.ql-container.ql-snow { border-color: var(--ui-border) !important; }
.recharts-tooltip-wrapper { background: #fff; border-radius: 8px; box-shadow: 0 4px 12px rgba(0,0,0,0.1); border: 1px solid var(--ui-border); }
`;


// --- MAIN APP COMPONENT ---
export default function Newguide() {
  const [activeTab, setActiveTab] = useState('foundation');
  const [selectedCard, setSelectedCard] = useState(null);


  // Download Handler: Serves the cleaned MASTER_CSS string
  const downloadCSS = () => {
    const element = document.createElement("a");
    const file = new Blob([MASTER_CSS], {type: 'text/css'});
    element.href = URL.createObjectURL(file);
    element.download = "master.css";
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };


  return (
    <div className="main-wrapper">
      <style>{MASTER_CSS}</style>


      {/* HEADER */}
      <header className="app-header">
        <div style={{display:'flex', alignItems:'center'}}>
           <img
             src="https://lywo.in/images/logo.svg"
             alt="Lywo Logo"
             className="app-logo-img"
           />
           <span className="badge bg-light text-dark border">v5.3 Unified</span>
        </div>
        <div>
          <button className="btn btn-primary" onClick={downloadCSS}>
            <Download size={16} /> Download .css
          </button>
        </div>
      </header>


      {/* NAVIGATION */}
      <nav className="app-nav">
        <button
          className={`app-nav-link ${activeTab === 'foundation' ? 'active' : ''}`}
          onClick={() => setActiveTab('foundation')}
        >
          <Type size={18} /> Foundation
        </button>
        <button
          className={`app-nav-link ${activeTab === 'components' ? 'active' : ''}`}
          onClick={() => setActiveTab('components')}
        >
          <LayoutGrid size={18} /> Components
        </button>
      </nav>


      {/* CONTENT */}
      <main className="layout-container">
       
        {/* TAB: FOUNDATION */}
        {activeTab === 'foundation' && (
          <div className="animate-fade-in">
            <SectionHeader title="Typography" description="Inter font family. Standardized for readability." />
            <div className="content-area">
              <div className="row">
                <div className="col-md-6">
                  <h1>Heading 1</h1>
                  <h2>Heading 2</h2>
                  <h3>Heading 3</h3>
                  <h4>Heading 4</h4>
                  <h5>Heading 5</h5>
                  <h6>Heading 6</h6>
                </div>
                <div className="col-md-6 d-flex flex-column gap-2">
                  <p className="text-base"><strong>Base Text (14px):</strong> Standard body copy.</p>
                  <p className="text-sm"><strong>Small Text (13px):</strong> Dense interfaces.</p>
                  <p className="text-xs"><strong>Extra Small (12px):</strong> Metadata/labels.</p>
                  <div style={{marginTop:'20px'}}>
                    <span className="text-primary fw-bold">.text-primary</span> &nbsp;
                    <span className="text-danger fw-bold">.text-danger</span> &nbsp;
                    <span className="text-muted fw-bold">.text-muted</span>
                  </div>
                </div>
              </div>
            </div>


            <SectionHeader title="Color Palette" description="Semantic color system." />
            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(200px, 1fr))', gap:'20px'}}>
              <ColorSwatch color="var(--brand-primary)" label="Primary" hex="#0d6efd" />
              <ColorSwatch color="var(--color-success-bg)" label="Success" hex="#d1e7dd" text="var(--color-success-text)" />
              <ColorSwatch color="var(--color-warning-bg)" label="Warning" hex="#fff3cd" text="var(--color-warning-text)" />
              <ColorSwatch color="var(--color-danger-bg)" label="Danger" hex="#f8d7da" text="var(--color-danger-text)" />
            </div>
          </div>
        )}


        {/* TAB: COMPONENTS */}
        {activeTab === 'components' && (
          <div className="animate-fade-in">
           
            {/* Buttons & Forms */}
            <div className="row mb-4">
              <div className="col-md-6">
                <SectionHeader title="Buttons" />
                <div className="content-area d-flex flex-wrap gap-2">
                  <button className="btn btn-primary">Primary Action</button>
                  <button className="btn btn-outline-primary">Secondary</button>
                  <button className="btn btn-primary" disabled>Disabled</button>
                  <button className="btn btn-icon"><Bell size={18}/></button>
                </div>
              </div>
              <div className="col-md-6">
                <SectionHeader title="Form Inputs" />
                <div className="content-area d-flex flex-column gap-3">
                  <div>
                    <label className="form-label">Email Address</label>
                    <input type="text" className="form-control" placeholder="name@example.com" />
                  </div>
                  <div className="d-flex gap-2">
                    <input type="text" className="otp-input" defaultValue="4" />
                    <input type="text" className="otp-input" defaultValue="2" />
                    <input type="text" className="otp-input" defaultValue="" />
                    <input type="text" className="otp-input" defaultValue="" />
                  </div>
                </div>
              </div>
            </div>


            {/* Cards */}
            <SectionHeader title="Cards & Selection" />
            <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(300px, 1fr))', gap:'20px', marginBottom:'40px'}}>
              <div className="card-std">
                <div style={{display:'flex', justifyContent:'space-between', marginBottom:'15px'}}>
                  <div className="quiz-img" style={{width:'40px', height:'40px', margin:0}}><FileText size={20}/></div>
                  <span className="text-xs text-muted">Static</span>
                </div>
                <h5>Standard Card</h5>
                <p className="text-sm text-muted">Basic content container.</p>
              </div>


              <div
                className={`card-std card-interactive ${selectedCard === 1 ? 'selected' : ''}`}
                onClick={() => setSelectedCard(1)}
              >
                <div style={{display:'flex', justifyContent:'space-between', marginBottom:'15px'}}>
                  <div className="quiz-img" style={{width:'40px', height:'40px', margin:0}}><Check size={20}/></div>
                  {selectedCard === 1 && <span className="text-primary text-xs fw-bold">Selected</span>}
                </div>
                <h5>Selectable Card</h5>
                <p className="text-sm text-muted">Click to toggle active state.</p>
              </div>


              <div className="quiz-box">
                <div className="quiz-img"><MessageSquare size={24}/></div>
                <h4>Assessment</h4>
                <p className="text-sm text-muted">Hover to reveal actions</p>
                <div className="quiz-footer">
                  <button className="btn btn-primary btn-sm">Start Quiz</button>
                </div>
              </div>
            </div>


            {/* Modules Row */}
            <div className="row mb-4">
              <div className="col-lg-8">
                <SectionHeader title="Kanban Board" />
                <div className="kanban-board">
                  <div className="kanban-col">
                    <div className="kanban-col-header">
                      <span>To Do</span><span className="text-muted">3</span>
                    </div>
                    <div className="kanban-item status-warning">
                      <h6 className="text-sm fw-bold">System Updates</h6>
                      <p className="text-xs text-muted mb-0">Due Tomorrow</p>
                    </div>
                    <div className="kanban-item status-danger">
                      <h6 className="text-sm fw-bold">Fix Critical Bug</h6>
                      <p className="text-xs text-muted mb-0">High Priority</p>
                    </div>
                  </div>
                  <div className="kanban-col">
                    <div className="kanban-col-header">
                      <span>In Progress</span><span className="text-muted">1</span>
                    </div>
                    <div className="kanban-item status-success">
                      <h6 className="text-sm fw-bold">Client Meeting</h6>
                      <p className="text-xs text-muted mb-0">Recording available</p>
                    </div>
                  </div>
                </div>
              </div>


              <div className="col-lg-4">
                <SectionHeader title="Chat Interface" />
                <div className="chat-window">
                  <div className="chat-topbar">
                    <span>Team Chat</span><User size={18} />
                  </div>
                  <div className="chat-viewport">
                    <div className="chat-msg received">Is the CSS ready? <div className="chat-timestamp">10:30</div></div>
                    <div className="chat-msg sent">Yes, optimized. <div className="chat-timestamp">10:32</div></div>
                  </div>
                  <div className="chat-composer">
                    <Paperclip size={20} className="text-muted" />
                    <input type="text" className="form-control border-0 bg-transparent p-0 shadow-none" placeholder="Message..." />
                    <button className="btn btn-primary p-2"><Send size={16}/></button>
                  </div>
                </div>
              </div>
            </div>


            {/* Upload & Media */}
            <div className="row">
              <div className="col-md-6">
                <SectionHeader title="File Upload" />
                <div className="dropzone">
                  <div className="dropzone-icon"><Download size={40} /></div>
                  <h6 className="text-primary">Click to upload</h6>
                  <p className="text-xs text-muted">SVG, PNG, JPG (max. 800x400px)</p>
                </div>
              </div>
              <div className="col-md-6">
                <SectionHeader title="Media Player" />
                <div className="video-player">
                  <PlayCircle size={60} style={{opacity:0.8}} />
                  <div className="video-controls">
                    <PlayCircle size={20} />
                    <div style={{flex:1, height:'4px', background:'rgba(255,255,255,0.3)', borderRadius:'2px'}}>
                      <div style={{width:'40%', height:'100%', background:'#fff'}}></div>
                    </div>
                    <span className="text-xs">02:30</span>
                  </div>
                </div>
              </div>
            </div>


          </div>
        )}
      </main>


      {/* FOOTER */}
      <footer className="app-footer">
        <a href="https://www.lywo.in" target="_blank" rel="noopener noreferrer" className="footer-link">
          <Globe size={14} /> www.lywo.in
        </a>
      </footer>
    </div>
  );
}


// --- Helpers ---
function SectionHeader({ title, description }) {
  return (
    <div style={{ marginBottom: '20px', paddingBottom: '10px', borderBottom: '1px solid #e2e8f0' }}>
      <h5 className="fw-bold" style={{ margin: 0, color: '#101828' }}>{title}</h5>
      {description && <p className="text-sm text-muted" style={{ margin: '4px 0 0 0' }}>{description}</p>}
    </div>
  );
}


function ColorSwatch({ color, label, hex, text }) {
  return (
    <div style={{ borderRadius: '12px', border: '1px solid #eaecf0', overflow: 'hidden' }}>
      <div style={{ height: '100px', background: color }}></div>
      <div style={{ padding: '12px' }}>
        <p className="fw-bold text-sm" style={{ marginBottom: '2px', color: text || 'inherit' }}>{label}</p>
        <p className="text-xs text-muted" style={{ margin: 0 }}>{hex}</p>
      </div>
    </div>
  );
}

